import type { BootstrapOptions } from 'mini-bootstrap';
import { errorThatWillCauseAlert } from 'owa-trace';
import { getBootstrapOptions, registerBootstrapOptions } from 'mini-app-module/lib/optionsRegistry';
import { getPackageBaseUrl } from 'owa-config';
import type { Module } from 'mini-common-schema';
import { moduleToConfigMap } from './configRegistry';
import { onModuleClick } from 'mini-module-switcher';
import { orchestrator } from 'satcheljs';
import { registerFontSubsets } from 'owa-icons';
import { registerRouterRoutes } from 'owa-router';
import { runAfterRequests } from './runAfterRequests';
import { trackModuleLargestSurfaceRender } from 'mini-calendar-bootstrap-config/lib/trackModuleLargestSurfaceRender';
import { updateModuleForVdirChange } from 'mini-app-module/lib/utils/updateModuleForVdirChange';

let isRunning = false;

orchestrator(onModuleClick, actionMessage => {
    changeModuleIfEnabled(actionMessage.module, actionMessage.currentlySelectedModule);
});

const resetIsRunning = () => {
    isRunning = false;
};

async function changeModuleIfEnabled(newModule: Module, currentlySelectedModule: Module) {
    if (currentlySelectedModule !== newModule && !isRunning) {
        isRunning = true;

        try {
            await changeModuleInternal(newModule);
        } finally {
            resetIsRunning();
        }
    }
}

async function changeModuleInternal(newModule: Module) {
    let options: BootstrapOptions | undefined = getBootstrapOptions(newModule);

    if (!options) {
        try {
            const lazyConfig = moduleToConfigMap[newModule];

            // call runAfterRequests
            runAfterRequests(newModule);

            options = await lazyConfig.options.importAndExecute(Promise.resolve(undefined));

            registerBootstrapOptions(options);

            /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion --
             * See aka.ms/mini-lint-errors
             *	> Forbidden non-null assertion.
             *	> Forbidden non-null assertion. */
            await registerRouterRoutes(options!.routerOptions!);

            const promisesToWaitOnBeforeSwitch = [];

            const statePromise = options.initializeState();
            statePromise && promisesToWaitOnBeforeSwitch.push(statePromise);

            const preRenderPromises = options?.prerenderPromises;
            if (preRenderPromises?.length) {
                promisesToWaitOnBeforeSwitch.push(
                    ...preRenderPromises.map(preRenderPromises => preRenderPromises.promise)
                );
            }

            /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion --
             * See aka.ms/mini-lint-errors
             *	> Forbidden non-null assertion. */
            registerFontSubsets(getPackageBaseUrl(), options!.iconFonts);

            /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion --
             * See aka.ms/mini-lint-errors
             *	> Forbidden non-null assertion. */
            const postLazyAction = options!.postLazyAction;
            if (postLazyAction) {
                /* eslint-disable-next-line prefer-spread, @typescript-eslint/no-non-null-assertion --
                 * undefined
                 * See aka.ms/mini-lint-errors
                 *	> Use the spread operator instead of '.apply()'.
                 *	> Forbidden non-null assertion. */
                postLazyAction.importAndExecute.apply(postLazyAction, options!.postLazyArgs || []);
            }

            await Promise.all(promisesToWaitOnBeforeSwitch);
        } catch (error) {
            errorThatWillCauseAlert(
                `[changeModuleInternal]: Error switching to module ${newModule}, ${error.message}`
            );
        }
    } else {
        // tracking for LSR
        trackModuleLargestSurfaceRender(
            newModule,
            null,
            true /** instant-switch -> decommission */,
            2 /** module-load-type */
        );
    }

    updateModuleForVdirChange(options);
}
